import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { routinesApi, type RoutineDto, type RoutineSuggestionDto } from '../../api/routines';
import { formatApiError } from '../../utils/errors';

/**
 * Automatizavimas — the ROUTINE BUILDER. Zone 1: active routines (pause/edit/delete,
 * draft state). Zone 2: "+ Nauja rutina" (blank builder form, cadence + weekday).
 * Zone 3: Pasiūlymai — the mined proposals that PREFILL the form (the accountant
 * always authors the final routine; nothing is auto-created, nothing posts without
 * the confirm click).
 */

interface FormState {
  kind: 'PayrollAccrual' | 'RecurringInvoice' | 'Reminder';
  reminderType: 'BankImport' | 'PeriodClose';
  graceDays: number;
  cadence: 'Monthly' | 'Weekly' | 'Annually';
  weekday: number;
  name: string;
  triggerDay: number;
  periodOffset: number;
  postingRule: string;
  templateJson: string;
  overdueDays: number;
}

const blankForm: FormState = {
  kind: 'PayrollAccrual', cadence: 'Monthly', weekday: 0, name: '', triggerDay: 3, periodOffset: -1,
  postingRule: 'MonthEnd', templateJson: '', overdueDays: 10,
  reminderType: 'BankImport', graceDays: 7,
};

export default function RoutinesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null); // null = closed
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [runInfo, setRunInfo] = useState<string | null>(null);

  const { data: routines, isLoading } = useQuery({ queryKey: ['routines'], queryFn: routinesApi.list });
  const { data: suggestions } = useQuery({
    queryKey: ['routine-suggestions'], queryFn: routinesApi.suggestions, staleTime: 300_000, retry: false,
  });

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ['routines'] });
    await queryClient.invalidateQueries({ queryKey: ['analytics-inbox'] });
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!form) throw new Error('no form');
      const templateJson = form.kind === 'Reminder'
        ? JSON.stringify({ templateVersion: 1, reminderType: form.reminderType, graceDays: form.graceDays })
        : form.templateJson;
      const payload = {
        kind: form.kind, name: form.name,
        cadence: form.cadence,
        triggerDayOfWeek: form.cadence === 'Weekly' ? form.weekday : null,
        triggerDayOfMonth: form.triggerDay,
        targetPeriodOffset: form.kind === 'Reminder' && form.reminderType === 'BankImport' ? 0 : form.periodOffset,
        postingDateRule: form.postingRule,
        templateJson, overdueAfterDays: form.overdueDays,
      };
      return editingId != null
        ? routinesApi.update(editingId, payload)
        : routinesApi.create(payload);
    },
    onSuccess: async () => {
      setError(null);
      setForm(null);
      setEditingId(null);
      await refresh();
    },
    onError: (err: Error) => setError(err.message || t('routines.saveFailed')),
  });

  const openBlank = () => { setForm({ ...blankForm }); setEditingId(null); };

  const openPrefilled = (s: RoutineSuggestionDto) => {
    const rt = /BankImport/.test(s.proposedTemplateJson) ? 'BankImport' : /PeriodClose/.test(s.proposedTemplateJson) ? 'PeriodClose' : 'BankImport';
    setForm({
      kind: s.kind === 'RecurringInvoice' ? 'RecurringInvoice' : s.kind === 'Reminder' ? 'Reminder' : 'PayrollAccrual',
      cadence: 'Monthly', weekday: 0,
      reminderType: rt, graceDays: 7,
      name: s.title,
      triggerDay: s.proposedTriggerDay,
      periodOffset: s.proposedPeriodOffset,
      postingRule: s.proposedPostingRule,
      templateJson: s.proposedTemplateJson,
      overdueDays: 10,
    });
    setEditingId(null);
  };

  const openEdit = (r: RoutineDto) => {
    setForm({
      kind: r.kind === 'RecurringInvoice' ? 'RecurringInvoice' : r.kind === 'Reminder' ? 'Reminder' : 'PayrollAccrual',
      cadence: r.cadence === 'Weekly' ? 'Weekly' : r.cadence === 'Annually' ? 'Annually' : 'Monthly',
      weekday: r.triggerDayOfWeek ?? 0,
      name: r.name, triggerDay: r.triggerDayOfMonth, periodOffset: r.targetPeriodOffset,
      postingRule: String(r.postingDateRule), templateJson: r.templateJson, overdueDays: r.overdueAfterDays,
      reminderType: /PeriodClose/.test(r.templateJson) ? 'PeriodClose' : 'BankImport',
      graceDays: Number(/"graceDays"\s*:\s*(\d+)/.exec(r.templateJson)?.[1] ?? 7),
    });
    setEditingId(r.id);
  };

  const runNow = async () => {
    try {
      const s = await routinesApi.run();
      setRunInfo(t('routines.runSummary', { checked: s.routinesChecked, premade: s.draftsPremade, alarms: s.alarms }));
      await refresh();
    } catch (err) {
      setRunInfo(t('routines.runFailed') + ' — ' + formatApiError(err));
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      <div className="py-2 border-b border-gray-100 flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-gray-900">{t('routines.title')}</h1>
          <p className="text-xs text-gray-400 mt-0.5">{t('routines.subtitle')}</p>
        </div>
        <button onClick={runNow}
                className="px-3 py-1.5 text-xs text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:border-primary-400">
          {t('routines.runNow')}
        </button>
        <button onClick={openBlank}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700">
          + {t('routines.newRoutine')}
        </button>
      </div>
      {runInfo && <p className="text-xs text-gray-500">{runInfo}</p>}

      {/* Zone 1: active routines */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-2">{t('routines.active')}</h2>
        {isLoading && <p className="text-sm text-gray-400 py-2">{t('common.loading')}</p>}
        {!isLoading && (routines ?? []).length === 0 && (
          <p className="text-sm text-gray-400 py-2">{t('routines.empty')}</p>
        )}
        <div className="space-y-2">
          {(routines ?? []).map((r: RoutineDto) => (
            <div key={r.id} className="flex items-center gap-3 border border-gray-100 rounded-lg px-3 py-2.5">
              <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full flex-shrink-0 ${
                r.status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                {r.status === 'Active' ? t('routines.statusActive') : t('routines.statusPaused')}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900 truncate">{r.name}</div>
                <div className="text-xs text-gray-400">
                  {t(`routines.kind.${r.kind}`)} · {t(`routines.cadence.${r.cadence}`)}
                  {r.cadence === 'Weekly' && r.triggerDayOfWeek != null ? ` (${t(`routines.weekday.${r.triggerDayOfWeek}`)})` : ` · ${t('routines.triggerDay')} ${r.triggerDayOfMonth}`} ·{' '}
                  {r.lastPremadePeriod
                    ? (r.lastDraftPosted
                        ? `${r.lastPremadePeriod} ✓ ${t('routines.draftPosted')}`
                        : `${r.lastPremadePeriod} — ${t('routines.draftPending')}`)
                    : t('routines.neverRan')}
                </div>
              </div>
              <button onClick={() => openEdit(r)} className="text-xs text-primary-600 hover:text-primary-800 font-medium px-2 py-1">
                {t('common.edit')}
              </button>
              <button
                onClick={async () => { await (r.status === 'Active' ? routinesApi.pause(r.id) : routinesApi.resume(r.id)); await refresh(); }}
                className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1">
                {r.status === 'Active' ? t('routines.pause') : t('routines.resume')}
              </button>
              <button
                onClick={async () => {
                  if (window.confirm(t('routines.deleteConfirm', { name: r.name }))) {
                    await routinesApi.remove(r.id);
                    await refresh();
                  }
                }}
                className="text-xs text-red-500 hover:text-red-700 px-2 py-1">
                {t('common.delete')}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Zone 3: suggestions (prefill the builder) */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h2 className="text-sm font-semibold text-gray-700 mb-1">{t('routines.suggestions')}</h2>
        <p className="text-xs text-gray-400 mb-2">{t('routines.suggestionsHint')}</p>
        {(suggestions ?? []).length === 0 && <p className="text-sm text-gray-400 py-1">{t('routines.noSuggestions')}</p>}
        <div className="space-y-2">
          {(suggestions ?? []).map((s: RoutineSuggestionDto, i: number) => (
            <div key={i} className="flex items-center gap-3 border border-primary-100 bg-primary-50/40 rounded-lg px-3 py-2.5">
              <span className="px-2 py-0.5 text-[11px] font-semibold rounded-full bg-primary-100 text-primary-800 flex-shrink-0">
                {t(`routines.kind.${s.kind}`)}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-gray-900">{s.title}</div>
                <div className="text-xs text-gray-500">{s.evidenceSummary}</div>
              </div>
              <button onClick={() => openPrefilled(s)}
                      className="px-3 py-1.5 text-xs font-medium text-primary-700 bg-white border border-primary-200 rounded-lg hover:border-primary-400">
                {t('routines.prefill')}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Zone 2: the builder form (blank / prefilled / editing) */}
      {form && (
        <div className="bg-white border border-primary-200 rounded-lg p-5 space-y-3">
          <h2 className="text-sm font-semibold text-gray-700">
            {editingId != null ? t('routines.editTitle') : t('routines.newTitle')}
          </h2>
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="text-sm">
              <span className="block text-xs text-gray-500 mb-1 font-medium">{t('routines.kindLabel')}</span>
              <select
                value={form.kind}
                onChange={e => setForm({ ...form, kind: e.target.value as FormState['kind'] })}
                disabled={editingId != null}
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white">
                <option value="PayrollAccrual">{t('routines.kind.PayrollAccrual')}</option>
                <option value="RecurringInvoice">{t('routines.kind.RecurringInvoice')}</option>
                <option value="Reminder">{t('routines.kind.Reminder')}</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500 mb-1 font-medium">{t('routines.cadenceLabel')}</span>
              <select
                value={form.cadence}
                onChange={e => setForm({ ...form, cadence: e.target.value as FormState['cadence'] })}
                disabled={editingId != null}
                className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white">
                <option value="Monthly">{t('routines.cadence.Monthly')}</option>
                <option value="Weekly">{t('routines.cadence.Weekly')}</option>
                <option value="Annually">{t('routines.cadence.Annually')}</option>
              </select>
            </label>
            {form.cadence === 'Weekly' ? (
              <label className="text-sm">
                <span className="block text-xs text-gray-500 mb-1 font-medium">{t('routines.weekdayLabel')}</span>
                <select
                  value={form.weekday}
                  onChange={e => setForm({ ...form, weekday: Number(e.target.value) })}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white">
                  {[0, 1, 2, 3, 4, 5, 6].map(i => (
                    <option key={i} value={i}>{t(`routines.weekday.${i}`)}</option>
                  ))}
                </select>
              </label>
            ) : (
              <label className="text-sm">
                <span className="block text-xs text-gray-500 mb-1 font-medium">{t('routines.triggerDay')}</span>
                <input type="number" min={1} max={28} value={form.triggerDay}
                       onChange={e => setForm({ ...form, triggerDay: Number(e.target.value) || 1 })}
                       className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
              </label>
            )}
            <label className="text-sm">
              <span className="block text-xs text-gray-500 mb-1 font-medium">{t('routines.nameLabel')}</span>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                     className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500 mb-1 font-medium">{t('routines.phaseLabel')}</span>
              <select value={form.periodOffset}
                      onChange={e => setForm({ ...form, periodOffset: Number(e.target.value) })}
                      disabled={editingId != null}
                      className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white">
                <option value={-1}>{t('routines.phase.lastMonth')}</option>
                <option value={0}>{t('routines.phase.currentMonth')}</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="block text-xs text-gray-500 mb-1 font-medium">{t('routines.overdueLabel')}</span>
              <input type="number" min={1} max={90} value={form.overdueDays}
                     onChange={e => setForm({ ...form, overdueDays: Number(e.target.value) || 10 })}
                     className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
            </label>
          </div>
          {form.kind === 'Reminder' ? (
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block text-xs text-gray-500 mb-1 font-medium">{t('routines.reminderTypeLabel')}</span>
                <select
                  value={form.reminderType}
                  onChange={e => setForm({ ...form, reminderType: e.target.value as FormState['reminderType'] })}
                  disabled={editingId != null}
                  className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm bg-white">
                  <option value="BankImport">{t('routines.reminderType.BankImport')}</option>
                  <option value="PeriodClose">{t('routines.reminderType.PeriodClose')}</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="block text-xs text-gray-500 mb-1 font-medium">{t('routines.graceDaysLabel')}</span>
                <input type="number" min={1} max={30} value={form.graceDays}
                       onChange={e => setForm({ ...form, graceDays: Number(e.target.value) || 7 })}
                       className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
                <span className="block text-[11px] text-gray-400 mt-1">{t('routines.graceDaysHint')}</span>
              </label>
            </div>
          ) : (
          <label className="text-sm block">
            <span className="block text-xs text-gray-500 mb-1 font-medium">{t('routines.templateLabel')}</span>
            <textarea value={form.templateJson}
                      onChange={e => setForm({ ...form, templateJson: e.target.value })}
                      rows={6} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs font-mono" />
            <span className="block text-[11px] text-gray-400 mt-1">{t('routines.templateHint')}</span>
          </label>
          )}
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex gap-2 justify-end">
            <button onClick={() => { setForm(null); setEditingId(null); setError(null); }}
                    className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
              {t('common.cancel')}
            </button>
            <button onClick={() => saveMutation.mutate()}
                    disabled={saveMutation.isPending || !form.name.trim() || (form.kind !== 'Reminder' && !form.templateJson.trim())}
                    className="px-4 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40">
              {saveMutation.isPending ? t('common.saving') : t('routines.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
